import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, ArrowUp, ArrowDown, X, Plus, AlertTriangle } from "lucide-react";
import { api, type ApiResponse } from "@/api/client";
import {
  HOMEPAGE_MONTHLY_HITS_MAX,
  HOMEPAGE_POPULAR_DESTINATIONS_MAX,
} from "@ckgo2/shared/schemas";
import { compareCzech, sortCzech } from "@ckgo2/shared";
import {
  DEFAULT_TRIP_TYPE_IMAGE_IDS,
  TRIP_TYPE_LABELS,
  TRIP_TYPES,
  type HomepageSettings,
  type Trip,
  type TripType,
} from "@ckgo2/shared/types";
import MediaPicker from "@/components/common/MediaPicker";
import { useAdminDestinationList, useAdminInspirationList } from "@/hooks/useAdminLists";
import { enumLabel } from "@/i18n/enumLabels";

interface EntityOption {
  id: string;
  label: string;
}

/** Zdroj voleb „Hity měsíce“: admin seznam zájezdů (prvních 200), volby abecedně (`sortCzech`). */
const HOMEPAGE_TRIP_OPTIONS_KEY = ["homepage-options", "/api/admin/trips"] as const;

function useTripOptions(): EntityOption[] {
  const { data } = useQuery({
    queryKey: HOMEPAGE_TRIP_OPTIONS_KEY,
    queryFn: () => api.get<ApiResponse<Trip[]>>("/api/admin/trips?limit=200"),
  });
  return useMemo(
    () =>
      sortCzech(
        (data?.data ?? []).map((trip) => ({ id: trip.id, label: trip.title || trip.id })),
        (o) => o.label,
      ),
    [data],
  );
}

/**
 * Destinace pro roletu i pro už zvolený seznam — JEDNA sdílená funkce popisku,
 * takže lokalita nese svou zemi („Sapa · Vietnam") na obou místech.
 *
 * Řádky jsou sdílený štíhlý admin seznam (`useAdminDestinationList`, všechny destinace
 * bez `limit`, stovky řádků včetně nepublikovaných seedových zemí), který už přichází česky
 * abecedně podle `name`. Roleta ale řadí podle složeného popisku a ve skupinách, takže se
 * řadí ještě tady: nejdřív země, pak lokality, uvnitř skupiny abecedně podle popisku
 * (`compareCzech`). Nepublikované jsou označené jako koncept.
 *
 * Koncepty se z rolety ZÁMĚRNĚ nevyhazují (přidat zemi před dopsáním obsahu je legitimní
 * pořadí kroků a chybějící položka v roletě je stejně tiché selhání jako chybějící karta),
 * ale nesou `isDraft` — vybraná položka pak u sebe má varování, že ji veřejná odpověď
 * odfiltruje (`routes/public/homepage.ts`), dokud se destinace nepublikuje.
 */
function useDestinationOptions(): Array<EntityOption & { group: string; isDraft: boolean }> {
  const { t } = useTranslation();
  const { data } = useAdminDestinationList();
  return useMemo(() => {
    const rows = data?.data ?? [];
    const nameById = new Map(rows.map((r) => [r.id, r.name || r.id]));
    return rows
      .map((r) => {
        const isCountry = !r.parentId;
        const parentName = r.parentId ? nameById.get(r.parentId) : undefined;
        const base = parentName ? `${r.name || r.id} · ${parentName}` : r.name || r.id;
        const isDraft = r.isPublished === false;
        return {
          id: r.id,
          label: isDraft ? `${base} (${t("content.homepage.draftSuffix")})` : base,
          group: isCountry
            ? t("content.homepage.groupCountries")
            : t("content.homepage.groupPlaces"),
          isCountry,
          isDraft,
        };
      })
      .sort((a, b) => Number(b.isCountry) - Number(a.isCountry) || compareCzech(a.label, b.label));
  }, [data, t]);
}

export default function HomepagePage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["settings", "homepage"],
    queryFn: () => api.get<ApiResponse<HomepageSettings>>("/api/admin/settings/homepage"),
  });

  const trips = useTripOptions();
  const { data: inspirationData } = useAdminInspirationList();
  const activities = useMemo<EntityOption[]>(
    () =>
      sortCzech(
        (inspirationData?.data ?? []).map((a) => ({ id: a.id, label: a.title || a.id })),
        (o) => o.label,
      ),
    [inspirationData],
  );
  const destinations = useDestinationOptions();

  /**
   * Horní lišta se z téhle stránky přestěhovala na `/promo-bar` (vlastní oprávnění, řez 04),
   * takže `promoBar` musí zmizet ze stavu, `defaultValues` i z JSX NARÁZ: PUT posílá celý
   * `form` a kategorie se ukládá `.partial()`, takže ponechané prázdné pole ve stavu by lištu
   * při každém uložení homepage vyplo.
   */
  const [form, setForm] = useState<Omit<HomepageSettings, "promoBar">>({
    heroTitle: "",
    heroSubtitle: "",
    heroBackgroundImageId: null,
    aboutHeroImageId: null,
    aboutHeroQuote: "",
    monthlyHits: [],
    popularDestinations: [],
    tripTypeImageIds: DEFAULT_TRIP_TYPE_IMAGE_IDS,
  });

  useEffect(() => {
    if (data?.data) {
      setForm({
        heroTitle: data.data.heroTitle ?? "",
        heroSubtitle: data.data.heroSubtitle ?? "",
        heroBackgroundImageId: data.data.heroBackgroundImageId ?? null,
        aboutHeroImageId: data.data.aboutHeroImageId ?? null,
        aboutHeroQuote: data.data.aboutHeroQuote ?? "",
        monthlyHits: data.data.monthlyHits ?? [],
        popularDestinations: data.data.popularDestinations ?? [],
        // `??` (ne `||`): uložený řádek s vyprázdněnými výběry je platná hodnota
        // („karta bez fotky"), default patří jen chybějícímu řádku.
        tripTypeImageIds: data.data.tripTypeImageIds ?? DEFAULT_TRIP_TYPE_IMAGE_IDS,
      });
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => api.put("/api/admin/settings/homepage", form),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["settings", "homepage"] }),
  });

  // Lookups: entity id -> label, for rendering selected lists.
  const tripLabel = useMemo(() => new Map(trips.map((t) => [t.id, t.label])), [trips]);
  const actLabel = useMemo(() => new Map(activities.map((a) => [a.id, a.label])), [activities]);
  // Celá volba (popisek + `isDraft`), aby vybraná nepublikovaná destinace mohla u sebe
  // nést varování — jen popisek by o tichém odfiltrování na webu nic neřekl.
  const destById = useMemo(() => new Map(destinations.map((d) => [d.id, d])), [destinations]);

  const typeLabels: Record<"trip" | "activity", string> = {
    trip: t("content.homepage.typeTrip"),
    activity: t("content.homepage.typeActivity"),
  };

  const move = <T,>(list: T[], i: number, dir: -1 | 1): T[] => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return list;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  };

  // Combined options for monthly hits (trips + activities), tagged by type.
  const hitOptions = useMemo(
    () => [
      ...trips.map((trip) => ({
        type: "trip" as const,
        id: trip.id,
        label: t("content.homepage.tripOption", { label: trip.label }),
      })),
      ...activities.map((a) => ({
        type: "activity" as const,
        id: a.id,
        label: t("content.homepage.inspirationOption", { label: a.label }),
      })),
    ],
    [trips, activities, t],
  );

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("content.homepage.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">{t("content.homepage.subtitle")}</p>
        </div>
        <button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="btn-primary"
        >
          {saveMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {saveMutation.isSuccess ? t("common.states.saved") : t("common.actions.save")}
        </button>
      </div>

      {/* Hero */}
      <section className="card space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t("content.homepage.heroTitle")}
        </h2>
        <div>
          <label className="label">{t("common.fields.title")}</label>
          <input
            value={form.heroTitle}
            onChange={(e) => setForm((f) => ({ ...f, heroTitle: e.target.value }))}
            className="input"
            maxLength={200}
          />
        </div>
        <div>
          <label className="label">{t("content.homepage.subtitleLabel")}</label>
          <input
            value={form.heroSubtitle}
            onChange={(e) => setForm((f) => ({ ...f, heroSubtitle: e.target.value }))}
            className="input"
            maxLength={300}
          />
        </div>
        <div>
          <label className="label">{t("content.homepage.backgroundImageLabel")}</label>
          <MediaPicker
            purpose="hero"
            mode="single"
            folder="general"
            value={form.heroBackgroundImageId}
            onChange={(id) => setForm((f) => ({ ...f, heroBackgroundImageId: id }))}
          />
        </div>
      </section>

      {/* Stránka O nás — úvodní fotka + citát (řez 11) */}
      <section className="card space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("content.homepage.aboutHeroTitle")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">{t("content.homepage.aboutHeroHint")}</p>
        </div>
        <div>
          <label className="label">{t("content.homepage.aboutHeroImageLabel")}</label>
          <MediaPicker
            purpose="hero"
            mode="single"
            folder="general"
            value={form.aboutHeroImageId}
            onChange={(id) => setForm((f) => ({ ...f, aboutHeroImageId: id }))}
          />
        </div>
        <div>
          <label className="label">{t("content.homepage.aboutHeroQuoteLabel")}</label>
          <textarea
            value={form.aboutHeroQuote}
            onChange={(e) => setForm((f) => ({ ...f, aboutHeroQuote: e.target.value }))}
            className="input"
            rows={3}
            maxLength={400}
          />
          <p className="mt-1 text-xs text-gray-500">{t("content.homepage.aboutHeroQuoteHint")}</p>
        </div>
      </section>

      {/* Fotky tří karet typů na úvodní stránce (vize H2) */}
      <section className="card space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("content.homepage.tripTypeImagesTitle")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">{t("content.homepage.tripTypeImagesHint")}</p>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {TRIP_TYPES.map((type: TripType) => (
            <div key={type}>
              <label className="label">{enumLabel(TRIP_TYPE_LABELS, type)}</label>
              <MediaPicker
                mode="single"
                folder="general"
                value={form.tripTypeImageIds[type]}
                onChange={(id) =>
                  setForm((f) => ({
                    ...f,
                    tripTypeImageIds: { ...f.tripTypeImageIds, [type]: id },
                  }))
                }
              />
            </div>
          ))}
        </div>
      </section>

      {/* Monthly hits */}
      <section className="card space-y-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t("content.homepage.monthlyHitsTitle")}
        </h2>
        <p className="text-sm text-gray-500">{t("content.homepage.monthlyHitsHint")}</p>
        <ul className="space-y-2">
          {form.monthlyHits.map((h, i) => (
            <li
              key={`${h.type}:${h.id}`}
              className="flex items-center gap-2 rounded-lg border border-gray-200 p-2 text-sm dark:border-gray-700"
            >
              <span className="flex-1 truncate">
                {h.type === "trip" ? tripLabel.get(h.id) : actLabel.get(h.id)}{" "}
                <span className="text-xs text-gray-400">({typeLabels[h.type] ?? h.type})</span>
              </span>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, monthlyHits: move(f.monthlyHits, i, -1) }))}
              >
                <ArrowUp className="h-4 w-4 text-gray-400 hover:text-gray-700" />
              </button>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, monthlyHits: move(f.monthlyHits, i, 1) }))}
              >
                <ArrowDown className="h-4 w-4 text-gray-400 hover:text-gray-700" />
              </button>
              <button
                title={t("common.actions.remove")}
                type="button"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    monthlyHits: f.monthlyHits.filter((_, idx) => idx !== i),
                  }))
                }
              >
                <X className="text-error-400 hover:text-error-600 h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
        {/* Mez vynucuje schéma při uložení (400 bez vysvětlení); na mezi se přidávání
            nenabízí a operátor vidí proč. */}
        {form.monthlyHits.length >= HOMEPAGE_MONTHLY_HITS_MAX ? (
          <p data-monthly-hits-limit className="text-xs text-amber-700 dark:text-amber-400">
            {t("content.homepage.monthlyHitsLimitReached", { max: HOMEPAGE_MONTHLY_HITS_MAX })}
          </p>
        ) : (
          <AddRefSelect
            options={hitOptions
              .filter((o) => !form.monthlyHits.some((h) => h.type === o.type && h.id === o.id))
              .map((o) => ({ value: `${o.type}:${o.id}`, label: o.label }))}
            onAdd={(value) => {
              const [type, id] = value.split(":") as ["trip" | "activity", string];
              setForm((f) =>
                f.monthlyHits.length >= HOMEPAGE_MONTHLY_HITS_MAX
                  ? f
                  : { ...f, monthlyHits: [...f.monthlyHits, { type, id }] },
              );
            }}
          />
        )}
      </section>

      {/* Popular destinations */}
      <section className="card space-y-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t("content.homepage.popularDestinationsTitle")}
        </h2>
        <p className="text-sm text-gray-500">{t("content.homepage.popularDestinationsHint")}</p>
        <ul className="space-y-2">
          {form.popularDestinations.map((d, i) => (
            <li
              key={d.id}
              className="flex items-center gap-2 rounded-lg border border-gray-200 p-2 text-sm dark:border-gray-700"
            >
              <div className="min-w-0 flex-1">
                <span className="block truncate">{destById.get(d.id)?.label ?? d.id}</span>
                {/* Nepublikovaná destinace se z veřejné odpovědi odfiltruje — bez tohohle
                    varování ji operátor v adminu vidí a na webu se nikdy neobjeví. */}
                {destById.get(d.id)?.isDraft && (
                  <span className="mt-0.5 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                    {t("content.homepage.draftWarning")}
                  </span>
                )}
              </div>
              <select
                value={d.size ?? "small"}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    popularDestinations: f.popularDestinations.map((x, idx) =>
                      idx === i ? { ...x, size: e.target.value as "large" | "small" } : x,
                    ),
                  }))
                }
                className="rounded border border-gray-300 px-1.5 py-0.5 text-xs dark:border-gray-600 dark:bg-gray-800"
              >
                <option value="large">{t("content.homepage.sizeLarge")}</option>
                <option value="small">{t("content.homepage.sizeSmall")}</option>
              </select>
              <button
                type="button"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    popularDestinations: move(f.popularDestinations, i, -1),
                  }))
                }
              >
                <ArrowUp className="h-4 w-4 text-gray-400 hover:text-gray-700" />
              </button>
              <button
                type="button"
                onClick={() =>
                  setForm((f) => ({ ...f, popularDestinations: move(f.popularDestinations, i, 1) }))
                }
              >
                <ArrowDown className="h-4 w-4 text-gray-400 hover:text-gray-700" />
              </button>
              <button
                title={t("common.actions.remove")}
                type="button"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    popularDestinations: f.popularDestinations.filter((_, idx) => idx !== i),
                  }))
                }
              >
                <X className="text-error-400 hover:text-error-600 h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
        {form.popularDestinations.length >= HOMEPAGE_POPULAR_DESTINATIONS_MAX ? (
          <p data-popular-destinations-limit className="text-xs text-amber-700 dark:text-amber-400">
            {t("content.homepage.popularDestinationsLimitReached", {
              max: HOMEPAGE_POPULAR_DESTINATIONS_MAX,
            })}
          </p>
        ) : (
          <AddRefSelect
            options={destinations
              .filter((d) => !form.popularDestinations.some((x) => x.id === d.id))
              .map((d) => ({ value: d.id, label: d.label, group: d.group }))}
            onAdd={(id) =>
              setForm((f) =>
                f.popularDestinations.length >= HOMEPAGE_POPULAR_DESTINATIONS_MAX
                  ? f
                  : {
                      ...f,
                      popularDestinations: [...f.popularDestinations, { id, size: "small" }],
                    },
              )
            }
          />
        )}
      </section>
    </div>
  );
}

interface AddRefOption {
  value: string;
  label: string;
  /** Volitelná skupina (`<optgroup>`); bez ní se roleta vykreslí plochá. */
  group?: string;
}

/**
 * A "choose entity → add" select that resets after each pick. Když mají položky
 * `group`, seskupí se do `<optgroup>` v pořadí prvního výskytu (volající je už
 * seřadil) — zájezdy a inspirace zůstávají ploché, prop je volitelný.
 */
function AddRefSelect({
  options,
  onAdd,
}: {
  options: AddRefOption[];
  onAdd: (value: string) => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  // Skupiny se staví VŽDY (nezařazené položky pod klíčem ""), větví se až obal `<optgroup>`.
  // Dřív měly obě větve vlastní kopii téhož `<option>` — pětiřádkový útvar dvakrát, takže
  // úprava položky rolety musela sáhnout na obě a plochá varianta se mohla rozejít tiše.
  const groups = useMemo(() => {
    const byGroup = new Map<string, AddRefOption[]>();
    for (const option of options) {
      const key = option.group ?? "";
      const list = byGroup.get(key);
      if (list) list.push(option);
      else byGroup.set(key, [option]);
    }
    return [...byGroup.entries()];
  }, [options]);
  const renderOption = (o: AddRefOption) => (
    <option key={o.value} value={o.value}>
      {o.label}
    </option>
  );
  return (
    <div className="flex gap-2">
      <select
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="input flex-1 text-sm"
      >
        <option value="">{t("content.homepage.addPlaceholder")}</option>
        {groups.map(([group, groupOptions]) =>
          group ? (
            <optgroup key={group} label={group}>
              {groupOptions.map(renderOption)}
            </optgroup>
          ) : (
            groupOptions.map(renderOption)
          ),
        )}
      </select>
      <button
        type="button"
        disabled={!value}
        onClick={() => {
          onAdd(value);
          setValue("");
        }}
        className="btn-ghost text-sm"
      >
        <Plus className="h-4 w-4" /> {t("common.actions.add")}
      </button>
    </div>
  );
}
